import "firebase/storage";

import { DefaultMap, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import { AppPlanCode, splitPlanCodeForAnalytics } from "common/app-plans";
import { BaseTheme } from "common/BaseTheme";
import {
    AppAuthentication,
    AppAuthenticationKind,
    AppDescription,
    AuthenticationEmailSource,
    IconImage,
    ScreenFlag,
    UserFeatures,
    defaultUserFeatures,
    getAppFeatures,
    getAppTabs,
    getSourceMetadata,
    newAppNonUserAppFeatures,
    getAppKind,
} from "common/components/SerializedApp";
import {
    AppEnvironment,
    AppOther,
    ComponentMoveAction,
    Context,
    Evaluable,
    EvaluatedScreen,
    ValueProducer,
} from "common/components/types";
import {
    AppGrandfathering,
    AppWithSchema,
    CompatibilityProblems,
    Database,
    UserData,
    getAppEminenceFlags,
    getEminenceFlagsForAppPlanCode,
    getUserFlags,
    isUserData,
    isolateCompatibilityProblems,
    listenToAppZaps,
    listenToTemplateSubmissions,
    loadAppAnalytics,
    loadAppGrandfathering,
    loadAppWithSchema,
    loadAppZapierAPIKey,
    loadPublishedApp,
    makeAppFeatures,
    removeIntegration,
    listenToAppWebhooks,
    unzipString,
    zipString,
    loadBuilderApp,
    BuilderApp,
    listenToPublishedAppMetadata,
    AppMetadata,
    loadAppMetadata,
    makeBuilderAppFromDocument,
} from "common/Database";
import {
    builderAppsCollectionName,
    documentIDForTableName,
    isSample,
    makeNotebooksDocumentPathForApp,
    TableName,
    tileDescriptionsCollectionName,
} from "common/database-strings";
import { BuilderAction, BuilderActionsForApp, loadActionsForApp } from "common/Database/builder-actions";
import { Eminence, eminenceFlags } from "common/Database/eminence";
import { isTileDescription, TileDescription } from "common/Database/tile-type";

import { CreateAppFromTemplateBody, Integration, UserFlags } from "common/firebase-function-types";
import {
    InteractiveComponentConfiguratorContext,
    makeSimpleComponentConfiguratorContext,
} from "common/generator/components";
import {
    ScreenDescriptionKind,
    TypeSchema,
    UserProfileDescription,
    findTable,
    getMenuScreenName,
    userProfileScreenName,
    areTableNamesEqual,
    isClassOrArrayScreenDescription,
    getSpanCoordsForComponents,
    getScreenComponentsArray,
    SpanCoordsFloat,
    TabDescription,
    getTableName,
} from "common/generator/description";
import { fixAppDescription } from "common/generator/fix-app";
import { makeUserProfileScreen } from "common/generator/make-screen";
import {
    assert,
    checkArray,
    checkString,
    defined,
    dontAwait,
    getResponseErrorMessage,
    isEmptyOrUndefined,
    isResponseOK,
    isUndefinedish,
    makeRandomPassword,
    sleep,
    updateDefined,
} from "common/Support";
import deepEqual from "deep-equal";
import {
    showAcceptCancelModal,
    showBuyOrDownloadTemplateModal,
    showErrorModal,
    showPaymentModal,
    showSuccessOrFailureModal,
} from "designer/ui/smart/modals/modals";
import { setTemplateSubmissions } from "reducers/template-submissions-state";
import { FirebaseAppUserAuthenticator } from "sharedUI/lib/app-user-authenticator";
import { v4 as uuid } from "uuid";
import { appStateWithNavigationModel } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { urlForApp } from "webapp/lib/appid-utils";

import { trackEvent } from "../analytics";
import { addApp, unloadOrganizations } from "../reducers/organizations";
import { buildAndEvaluateApp, buildAndEvaluateScreen } from "../lib/build-and-evaluate";
import * as firebaseAuth from "../lib/firebase-auth";
import { saveUserUpdates, setAppPlan, setAppPlanUnified } from "../lib/firebase-functions";
import { ForwardingAppEnvironment } from "../lib/forwarding-app-environment";
import { makeAppEnvironmentForAppDescription } from "../lib/make-env";
import { getBuilderAppDatabase, getBuilderUserDatabase } from "../lib/web-database";
import { downgradeToFree, setUser } from "../reducers/user-data";
import * as routes from "../routes";
import { loadedOrganizations } from "../store/loaded-redux-state";
import {
    PublishState,
    RootState,
    SourcePadState,
    TemplateStoreInfoForTemplate,
    getAppOwner,
    getCurrentAppAndState,
    getCurrentAppAndStateOrDefaults,
    NotebookWithTableName,
    Block,
} from "../store/types";
import { GlideAsyncThunk, GlideThunk } from "./action-thunk";
import {
    addTab,
    pushUndoState,
    setApp,
    setAppLoading,
    setReadOnlyApp,
    setTemplateStore,
    unloadApps,
    updateApp,
    updateComponentDescription,
    updateGlideAppState,
    updateScreenDescription,
    updateSerializedApp,
    updateTabDescription,
} from "./apps-actions";
import { logError, logInfo } from "common/debug-print";
import { AppNavigationModel } from "webapp/lib/navigation-model";
import { getScreenProperty, getTableProperty, makeScreenProperty } from "common/generator/components/property-utils";
import { isUserOrgID } from "designer/apps/orgs/orgs-lib";
import { forceUserProfileRow, getUserProfileTableInfo, UserProfileRow } from "common/components/user-profile";
import { asArrayValue, decomposeComponentID, isLoadedDefined } from "common/components/component-helpers";
import { getAppEnvironmentForAppID, setAppEnvironmentForAppID } from "sharedUI/app-state-context";
import { isFirstRunBuilderTemplate } from "designer/builder/lib/builder-first-run-templates";
import { copyClassOrArrayScreen } from "common/generator/copy-screen";
import { ValueListener } from "common/components/computation-types";
import { getPreviewAsKey, localStorageGetItem } from "webapp/lib/local-storage";
import { setNotebook } from "reducers/notebook-state";
import { withBusy } from "designer/designer";
import { setTileAnalytics, setTileDescriptionsForOwner } from "reducers/tile-descriptions-state";
import { PaymentRequestInfo } from "designer/modals/smart/payment-info-modal/stripe-pay-button";
import { makeDefaultClassScreen } from "common/generator/pages";

export function updateAppDescription(
    appEnvironment: ForwardingAppEnvironment,
    sourcePad: SourcePadState,
    appDesc: AppDescription,
    builderActions: BuilderActionsForApp,
    didModify: boolean
): ReturnType<typeof updateApp> {
    setAppEnvironmentForAppID(appEnvironment.appID, appEnvironment);
    return updateApp(appEnvironment.appID, {
        updates: {
            ...buildAndEvaluateApp(appEnvironment, sourcePad.schema, appDesc, builderActions),
            sourcePad,
        },
        didModify,
    });
}

export function makeScreenAndUpdateNavigationModel(
    appID: string,
    screenName: string,
    ctx: Context,
    title: Evaluable | undefined,
    flags: ReadonlySet<ScreenFlag> | undefined,
    itemID: string | undefined,
    update: (oldModel: AppNavigationModel, pivots: readonly EvaluatedScreen[]) => AppNavigationModel
): GlideThunk {
    return (dispatch, getStore) => {
        const appEnvironment = getAppEnvironmentForAppID(appID);
        const { sourcePad, serializedApp, builderActions, appState } = getCurrentAppAndStateOrDefaults(
            appID,
            getStore().apps
        );
        if (
            sourcePad === undefined ||
            appEnvironment === undefined ||
            serializedApp === undefined ||
            builderActions === undefined ||
            appState === undefined
        ) {
            return;
        }

        const { functions } = appState;
        const { schema } = sourcePad;
        const { pivots, userDefinedComponents } = buildAndEvaluateScreen(
            screenName,
            serializedApp,
            builderActions,
            schema,
            ctx,
            title,
            defined(appEnvironment),
            functions,
            flags,
            undefined,
            itemID,
            undefined,
            false
        );

        dispatch(
            updateGlideAppState(appID, oldState => {
                const navModel = oldState.navigationModel;
                const newModel = update(navModel, pivots);
                return appStateWithNavigationModel({ ...oldState, functions: userDefinedComponents }, newModel);
            })
        );
    };
}

// We're moving the source component to be relative to the target component.
// The target component stays where it is.
export function moveComponent(
    appID: string,
    sourceComponentID: string,
    targetComponentID: string,
    action: ComponentMoveAction
): GlideThunk {
    return (dispatch, getStore) => {
        const source = decomposeComponentID(sourceComponentID);
        const target = decomposeComponentID(targetComponentID);
        if (source === undefined || target === undefined) return;
        if (source.screenName !== target.screenName) return;
        const { screenName, indexes: sourceIndex } = source;
        const { indexes: targetIndex } = target;
        if (sourceIndex.length !== 1 || targetIndex.length !== 1) return;

        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
        if (serializedApp === undefined) return;

        const screen = serializedApp.screenDescriptions[screenName];
        if (!isClassOrArrayScreenDescription(screen)) return;

        const componentsWithCoords = getSpanCoordsForComponents(getScreenComponentsArray(screen));
        const sourceComponentWithCoords = componentsWithCoords[sourceIndex[0]];
        const targetComponentWithCoords = componentsWithCoords[targetIndex[0]];
        if (sourceComponentWithCoords === undefined || targetComponentWithCoords === undefined) return;

        const [sourceComponent] = sourceComponentWithCoords;
        const [targetComponent, targetCoords] = targetComponentWithCoords;

        if (targetComponent.spanCoordsFloat === undefined) {
            dispatch(updateComponentDescription(appID, screenName, targetComponent, { spanCoordsFloat: targetCoords }));
        }

        // function between(first: number, second: number | undefined)

        let newCoords: SpanCoordsFloat;
        if (action === ComponentMoveAction.NewColumnRight) {
            const componentsRight = componentsWithCoords.filter(
                ([, [s, c]]) => s === targetCoords[0] && c > targetCoords[1]
            );
            let column: number;
            if (componentsRight.length === 0) {
                column = targetCoords[1] + 128;
            } else {
                const nextColumn = Math.min(...componentsRight.map(([, [, c]]) => c));
                assert(nextColumn > targetCoords[1]);
                column = (targetCoords[1] + nextColumn) / 2;
            }

            newCoords = [targetCoords[0], column, 128];
        } else if (action === ComponentMoveAction.NewColumnLeft) {
            const componentsLeft = componentsWithCoords.filter(
                ([, [s, c]]) => s === targetCoords[0] && c < targetCoords[1]
            );
            let column: number;
            if (componentsLeft.length === 0) {
                column = targetCoords[1] - 128;
            } else {
                const prevColumn = Math.max(...componentsLeft.map(([, [, c]]) => c));
                assert(prevColumn < targetCoords[1]);
                column = (targetCoords[1] + prevColumn) / 2;
            }

            newCoords = [targetCoords[0], column, 128];
        } else if (action === ComponentMoveAction.InsertIntoColumnAfter) {
            const componentsBelow = componentsWithCoords.filter(
                ([, [s, c, i]]) => s === targetCoords[0] && c === targetCoords[1] && i > targetCoords[2]
            );
            let index: number;
            if (componentsBelow.length === 0) {
                index = targetCoords[2] + 128;
            } else {
                const nextIndex = Math.min(...componentsBelow.map(([, [, , i]]) => i));
                assert(nextIndex > targetCoords[2]);
                index = (targetCoords[2] + nextIndex) / 2;
            }
            newCoords = [targetCoords[0], targetCoords[1], index];
        } else if (action === ComponentMoveAction.InsertIntoColumnBefore) {
            const componentsAbove = componentsWithCoords.filter(
                ([, [s, c, i]]) => s === targetCoords[0] && c === targetCoords[1] && i < targetCoords[2]
            );
            let index: number;
            if (componentsAbove.length === 0) {
                index = targetCoords[2] - 128;
            } else {
                const prevIndex = Math.max(...componentsAbove.map(([, [, , i]]) => i));
                assert(prevIndex < targetCoords[2]);
                index = (targetCoords[2] + prevIndex) / 2;
            }
            newCoords = [targetCoords[0], targetCoords[1], index];
        } else if (action === ComponentMoveAction.NewSpanAbove) {
            const componentsAbove = componentsWithCoords.filter(([, [s]]) => s < targetCoords[0]);
            let span: number;
            if (componentsAbove.length === 0) {
                span = targetCoords[0] - 128;
            } else {
                const prevSpan = Math.max(...componentsAbove.map(([, [s]]) => s));
                assert(prevSpan < targetCoords[0]);
                span = (targetCoords[0] + prevSpan) / 2;
            }
            newCoords = [span, 128, 128];
        } else if (action === ComponentMoveAction.NewSpanBelow) {
            const componentsBelow = componentsWithCoords.filter(([, [s]]) => s > targetCoords[0]);
            let span: number;
            if (componentsBelow.length === 0) {
                span = targetCoords[0] + 128;
            } else {
                const nextSpan = Math.min(...componentsBelow.map(([, [s]]) => s));
                assert(nextSpan > targetCoords[0]);
                span = (targetCoords[0] + nextSpan) / 2;
            }
            newCoords = [span, 128, 128];
        } else {
            return;
        }

        dispatch(
            updateComponentDescription(appID, screenName, sourceComponent, {
                spanCoordsFloat: newCoords,
            })
        );
    };
}

export function addMenuScreen(
    appID: string,
    menuID: string,
    primaryKeyHash: string,
    defaultScreenName: string
): GlideThunk {
    return (dispatch, getStore) => {
        const {
            sourcePad,
            serializedApp,
            builderActions: compoundActions,
        } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
        if (sourcePad === undefined || serializedApp === undefined || compoundActions === undefined) return;

        const { schema } = sourcePad;

        const defaultScreen = serializedApp.screenDescriptions[defaultScreenName];
        if (defaultScreen === undefined) {
            logError("Default screen does not exist", defaultScreenName);
            return;
        }
        if (defaultScreen.kind !== ScreenDescriptionKind.Class) {
            logError("Default screen is not a class screen", defaultScreenName, defaultScreen);
            return;
        }

        const table = findTable(schema, defaultScreen.type);
        if (table === undefined) {
            logError("Menu screen table does not exist", defaultScreen.type);
            return;
        }

        const ccc = makeSimpleComponentConfiguratorContext(
            appID,
            serializedApp,
            compoundActions,
            schema,
            defaultUserFeatures,
            eminenceFlags[Eminence.Free]
        );
        const screen = makeDefaultClassScreen(table, ccc, false);

        dispatch(updateScreenDescription(appID, screen, getMenuScreenName(menuID, primaryKeyHash), true));
    };
}

export function setAppTitle(appID: string, title: string): GlideThunk {
    return (dispatch, getStore) => {
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
        if (serializedApp === undefined) return;

        dispatch(updateSerializedApp(appID, { title }));

        // Change tab title if there's only one
        const tabs = getAppTabs(serializedApp);
        if (tabs.length === 1 && tabs[0].propertyName === undefined) {
            dispatch(updateTabDescription(appID, 0, { ...tabs[0], title }, false));
        }
    };
}

export function setAppDescriptionText(appID: string, description: string): GlideThunk {
    return dispatch => {
        dispatch(updateSerializedApp(appID, { description }));
    };
}

export function setAppAuthentication(appID: string, authentication: AppAuthentication | undefined): GlideThunk {
    return dispatch => {
        dispatch(updateSerializedApp(appID, { authentication }));
    };
}

export function updateTheme(appID: string, updates: Partial<BaseTheme>): GlideThunk {
    return (dispatch, getStore) => {
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
        if (serializedApp === undefined) return;
        dispatch(updateSerializedApp(appID, { theme: { ...serializedApp.theme, ...updates } }));
    };
}

export function setIcon(appID: string, iconImage: IconImage): GlideThunk {
    return dispatch => {
        dispatch(updateSerializedApp(appID, { iconImage }));
    };
}

function updateUserProfileScreen(appID: string, keepComponents: boolean): GlideThunk {
    return (dispatch, getStore) => {
        const appEnvironment = getAppEnvironmentForAppID(appID);
        if (appEnvironment === undefined) return;

        const { serializedApp, sourcePad } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);

        appEnvironment.updateGlobalVariables();

        if (serializedApp === undefined || sourcePad === undefined) return;
        const { userProfile } = serializedApp;

        const screenDesc = makeUserProfileScreen(
            userProfile,
            sourcePad.schema,
            keepComponents,
            serializedApp,
            undefined
        );
        dispatch(updateScreenDescription(appID, screenDesc ?? false, userProfileScreenName, true));
    };
}

export function updateOrSetUserProfileDescription(
    appID: string,
    update: (old: UserProfileDescription | undefined, schema: TypeSchema) => UserProfileDescription | undefined
): GlideThunk {
    return (dispatch, getStore) => {
        const { serializedApp, sourcePad } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
        if (serializedApp === undefined || sourcePad === undefined) return;
        const { schema } = sourcePad;
        const old = serializedApp.userProfile;
        const userProfile = update(old, schema);
        if (userProfile === old) return;
        const oldTableName = getTableProperty(old?.userProfileTable);
        const newTableName = getTableProperty(userProfile?.userProfileTable);
        const keepComponents =
            oldTableName !== undefined && newTableName !== undefined && areTableNamesEqual(oldTableName, newTableName);

        dispatch(pushUndoState(appID));
        dispatch(updateSerializedApp(appID, { userProfile }));
        dispatch(updateUserProfileScreen(appID, keepComponents));
    };
}

export function duplicateTab(
    appID: string,
    tabIndex: number,
    iccc: InteractiveComponentConfiguratorContext
): GlideAsyncThunk {
    return async (dispatch, getStore) => {
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, getStore().apps);

        const tabs = serializedApp?.tabs;
        if (tabs === undefined) return;
        const td = tabs[tabIndex];
        if (td === undefined) return;

        const screenName = getScreenProperty(td.screenName);
        if (screenName === undefined || serializedApp?.screenDescriptions[screenName] === undefined) return;

        const screen = serializedApp.screenDescriptions[screenName];
        if (!isClassOrArrayScreenDescription(screen)) return;

        const screensCreated = new Set<string>();
        const screenCopy = await copyClassOrArrayScreen(screen, iccc, undefined, screensCreated);
        if (screenCopy === undefined) return;

        const newScreenName = iccc.addFreeScreen(screensCreated, () => screenCopy);

        const tabCopy: TabDescription = {
            ...td,
            screenName: makeScreenProperty(newScreenName),
        };

        const newTabs = [...tabs];
        newTabs.splice(tabIndex + 1, 0, tabCopy);

        dispatch(addTab(appID, tabCopy, tabIndex + 1));
    };
}

export async function getPublishState(db: Database, id: string): Promise<PublishState> {
    const app = await loadPublishedApp(db, id);
    const builderApp: BuilderApp | undefined = await loadBuilderApp(db, id);

    const isPublished = app !== undefined;
    const appURL = isPublished ? urlForApp(id, app) : undefined;
    const shortName = definedMap(app, a => a.shortName);
    const customDomain = definedMap(app, a => a.customDomain);
    const desiredDomain = definedMap(app, a => a.desiredDomain);
    const isPublishingActive = builderApp?.isPublishingActive ?? true;
    const publishedAt = app?.publishedAt;
    return {
        publishing: false,
        appURL,
        error: undefined,
        shortName,
        customDomain,
        isPublished,
        desiredDomain,
        isPublishingActive,
        publishedAt,
    };
}

export async function getAppMetadata(db: Database, id: string): Promise<AppMetadata> {
    const app = await loadAppMetadata(db, id);
    return app ?? {};
}

function publishStateFromStore(store: RootState, appID: string): PublishState | undefined {
    const curAppState = store.apps[appID];
    if (curAppState === "loading" || curAppState === undefined) return undefined;
    const { publishState } = curAppState;
    return publishState === "loading" ? undefined : publishState;
}

export function setShortNameForAppID(appID: string, shortName: string): GlideThunk {
    return (dispatch, getStore) => {
        const publishState = publishStateFromStore(getStore(), appID);
        if (publishState === undefined) return;
        dispatch(updateApp(appID, { updates: { publishState: { ...publishState, shortName } }, didModify: false }));
    };
}

export function setIsPublishingActive(appID: string, isPublishingActive: boolean): GlideThunk {
    return (dispatch, getStore) => {
        const publishState = publishStateFromStore(getStore(), appID);
        if (publishState === undefined) return;
        dispatch(
            updateApp(appID, { updates: { publishState: { ...publishState, isPublishingActive } }, didModify: false })
        );
    };
}

export function setCustomDomainForAppID(appID: string, customDomain: string): GlideThunk {
    return (dispatch, getStore) => {
        const publishState = publishStateFromStore(getStore(), appID);
        if (publishState === undefined) return;
        dispatch(updateApp(appID, { updates: { publishState: { ...publishState, customDomain } }, didModify: false }));
    };
}

export function setDesiredDomainForAppID(appID: string, desiredDomain: string): GlideThunk {
    return (dispatch, getStore) => {
        const publishState = publishStateFromStore(getStore(), appID);
        if (publishState === undefined) return;
        dispatch(updateApp(appID, { updates: { publishState: { ...publishState, desiredDomain } }, didModify: false }));
    };
}

export function loadAppAnalyticsForApp(appID: string): GlideAsyncThunk {
    return async dispatch => {
        logInfo("loading analytics", appID);

        const db = getBuilderAppDatabase(appID);

        const analytics = await loadAppAnalytics(db, appID);
        if (analytics === undefined) return;

        dispatch(updateApp(appID, { updates: { analytics }, didModify: false }));
    };
}

export function loadZapierAPIKeyForApp(appID: string): GlideAsyncThunk {
    return async dispatch => {
        logInfo("loading Zapier API Key", appID);

        const db = getBuilderAppDatabase(appID);

        const zapierAPIKey = await loadAppZapierAPIKey(db, appID);
        if (zapierAPIKey === undefined) return;

        dispatch(updateApp(appID, { updates: { zapierAPIKey }, didModify: false }));
    };
}

function listenToZapsForApp(appID: string): GlideThunk<() => void> {
    return dispatch => {
        logInfo("loading zaps", appID);

        const db = getBuilderAppDatabase(appID);

        return listenToAppZaps(db, appID, zaps => {
            dispatch(updateApp(appID, { updates: { zaps }, didModify: false }));
        });
    };
}

function listenToWebhooksForApp(appID: string): GlideThunk<() => void> {
    return dispatch => {
        logInfo("loading webhooks", appID);

        const db = getBuilderAppDatabase(appID);

        return listenToAppWebhooks(db, appID, webhooks => {
            dispatch(updateApp(appID, { updates: { webhooks }, didModify: false }));
        });
    };
}

export function listenToPublishedAppMetadataForApp(appID: string): GlideThunk<() => void> {
    return dispatch => {
        const db = getBuilderAppDatabase(appID);

        return listenToPublishedAppMetadata(db, appID, appMetadata => {
            dispatch(
                updateApp(appID, {
                    updates: { appMetadata: appMetadata },
                    didModify: false,
                })
            );
        });
    };
}

export function listenToTemplateSubmissionsForApp(appID: string): GlideThunk<() => void> {
    return dispatch => {
        logInfo("loading template submissions", appID);

        const db = getBuilderAppDatabase(appID);

        return listenToTemplateSubmissions(db, appID, ts => {
            dispatch(setTemplateSubmissions(ts));
        });
    };
}

const tileListenPromises = new Map<string, Promise<void>>();

export function loadTileDescriptionFromFirebase(ownerID: string): GlideAsyncThunk {
    return (dispatch, getState) => {
        const maybePromise = tileListenPromises.get(ownerID);
        if (maybePromise !== undefined) return maybePromise;

        const { userData } = getState();

        const promise = new Promise<void>(resolve => {
            if (!isUserData(userData)) {
                logError("Trying to load tiles before user is loaded");
                return;
            }

            let didResolve = false;
            const db = getBuilderUserDatabase(ownerID);
            // FIXME: We never unlisten
            db.listenWhere(
                tileDescriptionsCollectionName,
                [
                    {
                        fieldPath: "authIDs",
                        opString: "array-contains",
                        value: userData.authID,
                    },
                    {
                        fieldPath: "userID",
                        opString: "==",
                        value: ownerID,
                    },
                ],
                undefined,
                undefined,
                undefined,
                results => {
                    const tiles: [string, TileDescription][] = mapFilterUndefined(results, ({ id, data }) => {
                        if (!isTileDescription(data)) return undefined;
                        return [id, data];
                    });
                    dispatch(setTileDescriptionsForOwner({ ownerID, tiles }));
                    tiles.forEach(async t => {
                        const a = await loadAppAnalytics(db, t[0]);
                        if (a !== undefined) {
                            dispatch(
                                setTileAnalytics({
                                    ownerID,
                                    appID: t[0],
                                    analytics: a,
                                })
                            );
                        }
                    });
                    if (!didResolve) {
                        didResolve = true;
                        resolve();
                    }
                }
            );
        });
        tileListenPromises.set(ownerID, promise);
        return promise;
    };
}

export class RowAwaiter implements ValueListener<AppOther, AppEnvironment> {
    constructor(
        private readonly _rowsVP: ValueProducer,
        private readonly _resolve: (rows: readonly ValueProducer[]) => void
    ) {
        _rowsVP.subscribe(this);
    }

    public setDirty(sender: ValueProducer): void {
        assert(sender === this._rowsVP);

        const { current } = this._rowsVP;
        if (!isLoadedDefined(current)) return;

        const rows = asArrayValue(current);
        if (rows.length === 0) return;

        this._rowsVP.unsubscribe(this);
        this._resolve(rows);
    }
}

async function setDefaultPreviewAsUser(appDescription: AppDescription, appEnvironment: AppEnvironment): Promise<void> {
    const info = getUserProfileTableInfo(appDescription);
    if (info === undefined) return;

    const { dataStore } = appEnvironment;

    const rowsVP = dataStore.fetchTableRows(info.tableName, true);
    if (rowsVP === undefined) {
        logError("No rows VP for user profile table");
        return;
    }

    const rows = await new Promise<readonly ValueProducer[]>(resolve => new RowAwaiter(rowsVP, resolve));
    assert(rows.length > 0);

    let userRow: UserProfileRow | undefined;
    for (const rowVP of rows) {
        userRow = forceUserProfileRow(rowVP, "dummy", info, vp => vp.current);
        if (userRow !== undefined) break;
    }

    if (userRow === undefined) {
        logInfo("No user profile found");
        return;
    }

    appEnvironment.authenticator.setEmailAddressFromBuilder(userRow.email, false);
}

function loadNotebooksForApp(db: Database, appID: string, schema: TypeSchema): GlideAsyncThunk {
    return async dispatch => {
        const path = makeNotebooksDocumentPathForApp(appID);

        // set all to "loading" first
        for (const table of schema.tables) {
            const tableName = getTableName(table);
            if (tableName.isSpecial) continue;

            dispatch(setNotebook({ tableName, blocks: undefined }));
        }

        for (const table of schema.tables) {
            const tableName = getTableName(table);
            if (tableName.isSpecial) continue;

            const notebook = await db.getDocument(path, documentIDForTableName(tableName));
            const blocks = definedMap(notebook?.blocks, b => checkArray(JSON.parse(unzipString(b)))) ?? [];

            dispatch(setNotebook({ tableName, blocks: blocks as Block[] }));
        }
    };
}

const notebooksToSave = new DefaultMap<string, Map<string, NotebookWithTableName>>(() => new Map());

async function saveNotebookLoop(db: Database, appID: string, tableName: TableName): Promise<void> {
    const path = makeNotebooksDocumentPathForApp(appID);
    let lastNotebook: NotebookWithTableName | undefined;
    const documentID = documentIDForTableName(tableName);

    for (;;) {
        const notebook = defined(notebooksToSave.get(appID).get(tableName.name));
        if (notebook === lastNotebook) {
            notebooksToSave.get(appID).delete(tableName.name);
            return;
        }

        await db.setDocument(path, documentID, { blocks: zipString(JSON.stringify(notebook.blocks)) });

        lastNotebook = notebook;
    }
}

export function saveNotebookForApp(appID: string, book: NotebookWithTableName): GlideThunk {
    return dispatch => {
        const { tableName } = book;

        const db = getBuilderAppDatabase(appID);
        const forApp = notebooksToSave.get(appID);
        const isRunning = forApp.has(tableName.name);
        forApp.set(tableName.name, book);

        if (!isRunning) {
            dontAwait(saveNotebookLoop(db, appID, tableName));
        }

        dispatch(setNotebook(book));
    };
}

export function updateAppSerial(appID: string, serial: number): GlideThunk {
    return (dispatch, getStore) => {
        const { apps } = getStore();
        const curApp = apps[appID];

        if (curApp === undefined || curApp === "loading") return;

        dispatch(
            setApp(appID, {
                ...curApp,
                serial: Math.max(curApp.serial, serial),
            })
        );
    };
}

// FIXME: We need to audit all occurences of subscriptions that we do not unsubscribe from
// For example. listenToZapsForApp and listenToTemplateSubmissionsForApp
export function loadAppFromFirebase(
    appID: string,
    reload: boolean,
    withListen: boolean
): GlideAsyncThunk<(() => void) | undefined> {
    return async (dispatch, getStore) => {
        try {
            const appFacilities = getAppFacilities();
            const db = getBuilderAppDatabase(appID);

            const { apps, userData } = getStore();
            if (apps[appID] !== undefined && !reload) return undefined;

            logInfo("loading app from firebase", appID);

            dispatch(setAppLoading(appID));

            let maybeApp: AppWithSchema | undefined;
            let appGrandfathering: AppGrandfathering | undefined;
            let builderActions: Map<string, BuilderAction> | undefined;
            let isPublished = false;

            for (let i = 0; i < 200; i++) {
                // 20 seconds ought to be enough for anyone.

                // We do this try/catch because we might be asked to load
                // an app before the Firebase user is set up, which throws
                // a permission exception.

                // FIXME: It is possible to wait on the Firebase Auth state.
                // We should do that instead.
                try {
                    const [appWithSchema, grandfathering, publishedApp, actions] = await Promise.all([
                        loadAppWithSchema(db, appID),
                        loadAppGrandfathering(db, appID),
                        loadPublishedApp(db, appID),
                        loadActionsForApp(db, appID, await appFacilities.getAuthUserID()),
                    ]);
                    maybeApp = appWithSchema;
                    appGrandfathering = grandfathering;
                    builderActions = actions;
                    if (publishedApp !== undefined) {
                        isPublished = true;
                    }
                } catch {
                    await sleep(100);
                    continue;
                }
                break;
            }

            logInfo("loaded from firebase", appID, maybeApp);
            if (maybeApp === undefined || builderActions === undefined) return undefined;

            const {
                builderApp: {
                    appDescription,
                    templatePurchaseInformation,
                    templateAuthorInformation,
                    serial: maybeSerial,
                },
                publishedAppData,
                schema,
                userID,
            } = maybeApp;
            const pricing = templatePurchaseInformation?.pricing;

            const appOwner = getAppOwner(appID, userData, await loadedOrganizations());

            // appOwner is expected to be undefined here in two cases:
            // 1. It's a template, so its eminence is assumed to be Pro.
            // 2. We are an admin looking at a user's app, so the eminence doesn't really matter.
            // If neither of these were true, but appOwner were undefined, you didn't have
            // permission to read the app anyway and would have already bailed out.

            const appEminenceFlags =
                appOwner !== undefined ? getAppEminenceFlags(appOwner, appID, isPublished) : eminenceFlags.pro;
            const fixedAppDescription = fixAppDescription(
                appID,
                appDescription,
                builderActions,
                schema,
                appEminenceFlags
            );
            const sourcePad: SourcePadState = {
                sourceMetadata: getSourceMetadata(fixedAppDescription),
                spreadsheetName: definedMap(publishedAppData, d => d.spreadsheetName),
                schema,
            };
            const appFeatures = makeAppFeatures(
                appOwner,
                appID,
                isPublished,
                appGrandfathering,
                getAppFeatures(fixedAppDescription),
                undefined
            );
            // FIXME: Can we do this without evaluating the app?
            const authenticator = new FirebaseAppUserAuthenticator(appFacilities, appID);
            const appEnvironment = makeAppEnvironmentForAppDescription(
                fixedAppDescription,
                schema,
                authenticator,
                appFeatures,
                true
            );

            dontAwait(dispatch(loadNotebooksForApp(db, appID, schema)));

            // ##previewAsUser:
            // This is the place where we set the preview-as user to either
            // whatever the user selected last - stored in local storage - or
            // to the "default" one, which is the first user profile we find
            // in the app, if there is one.
            const previewAsEmail = localStorageGetItem(getPreviewAsKey(appID));
            if (previewAsEmail !== undefined) {
                setTimeout(() => {
                    appEnvironment.authenticator.setEmailAddressFromBuilder(previewAsEmail, true);
                }, 0);
            } else {
                dontAwait(setDefaultPreviewAsUser(fixedAppDescription, appEnvironment));
            }

            let storeInfo: TemplateStoreInfoForTemplate | undefined;
            const { title } = fixedAppDescription;
            if (pricing !== undefined) {
                const author = templateAuthorInformation?.name;
                const email = templateAuthorInformation?.email;
                storeInfo = { title, author, email, pricing };
            } else {
                // Any template that does not have pricing information set
                // is assumed to be one of the "second generation" templates
                // created by Glide.
                storeInfo = { title, author: "Glide", email: undefined, pricing: { currency: "USD", value: 0 } };
            }

            // Ignore _all_ linter complaints about this await. It is important.
            // The template store information needs to be established _first_,
            // before the serialized app ever shows up, because copySampleApp
            // relies very much on that behavior.
            await dispatch(setTemplateStore(appID, storeInfo));

            const [publishState, appMetadata] = await Promise.all([
                getPublishState(db, appID),
                getAppMetadata(db, appID),
            ]);

            // This exists to mitigate a race condition between this and
            // createBuilderApp; if the network connection is too slow we'll
            // start showing the builder _before_ we've had a chance to reload
            // from Firestore, which means that any edits made between then
            // and now will be lost.
            const priorAppState = getCurrentAppAndStateOrDefaults(appID, getStore().apps);

            const evaluatedApp = buildAndEvaluateApp(
                appEnvironment,
                sourcePad.schema,
                fixedAppDescription,
                builderActions
            );
            let serial = maybeSerial;
            if (priorAppState.serializedApp !== undefined) {
                evaluatedApp.serializedApp = priorAppState.serializedApp;
            }
            if (priorAppState.appState !== undefined) {
                evaluatedApp.appState = priorAppState.appState;
            }
            if (priorAppState.serial !== undefined) {
                // If we didn't preserve this, it would be possible for the
                // apps-saver to complete a write and update the serial
                // _after_ we've managed to load again from the database,
                // which would bump the serial back in time and result
                // in a failed save.
                serial = priorAppState.serial;
            }

            dispatch(
                setApp(appID, {
                    ...evaluatedApp,
                    builderActions,
                    sourcePad,
                    publishState,
                    appMetadata,
                    reloadIssues: definedMap(publishedAppData, isolateCompatibilityProblems),
                    analytics: undefined,
                    userID,
                    zapierAPIKey: undefined,
                    zaps: [],
                    webhooks: [],
                    quotas: undefined,
                    grandfathering: appGrandfathering,
                    // This can be undefined for older apps. Note that, generally, this shouldn't be undefined.
                    serial: serial ?? 0,
                })
            );
            setAppEnvironmentForAppID(appID, appEnvironment);

            // This has to happen after setting the app and the app
            // environment, because setting the zapier API key at least
            // depends on both of them being set.
            const dispatches = [dispatch(loadAppAnalyticsForApp(appID)), dispatch(loadZapierAPIKeyForApp(appID))];
            await Promise.all(dispatches);

            // These all return unlisten functions
            const zapsUnlisten = dispatch(listenToZapsForApp(appID));
            const webhooksUnlisten = dispatch(listenToWebhooksForApp(appID));
            const metadataUnlisten = dispatch(listenToPublishedAppMetadataForApp(appID));

            let templateUnlisten: (() => void) | undefined;

            // This will be unauthorized if the appID is a template, or we're
            // an admin or support.
            if (!isSample(appID) && appOwner !== undefined) {
                templateUnlisten = dispatch(listenToTemplateSubmissionsForApp(appID));
            }

            let builderAppUnlisten: (() => void) | undefined;
            if (withListen) {
                // FIXME: we're never unlistening
                builderAppUnlisten = db.listenToDocument(
                    builderAppsCollectionName,
                    appID,
                    doc => {
                        const builderApp = makeBuilderAppFromDocument(db, doc);
                        dispatch(setReadOnlyApp(appID, builderApp.appDescription));
                    },
                    e => {
                        logError("Error listening to builder app", e);
                    }
                );
            }

            return () => {
                logInfo("unlistening");
                zapsUnlisten();
                webhooksUnlisten();
                metadataUnlisten();
                templateUnlisten?.();
                builderAppUnlisten?.();
            };
        } catch (e: unknown) {
            logError("Could not load or evaluate app", e);
        }
        return undefined;
    };
}

export function setUserFlags(userData: UserData, flags: Partial<UserFlags>): GlideThunk {
    return dispatch => {
        const newUser: UserData = {
            ...userData,
            flags: updateDefined(getUserFlags(userData), flags),
        };
        dispatch(setUser(newUser));
        dontAwait(saveUserUpdates({ flags }, {}));
    };
}

export function removeUserIntegration(integration: Integration): GlideAsyncThunk {
    return async (dispatch, getStore) => {
        const userData = getStore().userData;
        if (isUndefinedish(userData) || userData === "loading") return;

        const integrations = removeIntegration(userData.integrations, integration);
        dispatch(setUser({ ...userData, integrations }));
        await saveUserUpdates({ integrations }, { integrations: [integration] });
    };
}

export function toggleAppWorldCopyable(appID: string, isWorldCopyable: boolean): GlideThunk {
    return (dispatch, getStore) => {
        const store = getStore();
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, store.apps);
        if (serializedApp === undefined) return;
        dispatch(updateSerializedApp(appID, { features: { ...getAppFeatures(serializedApp), isWorldCopyable } }));
    };
}

export function setTabletModeDefault(appID: string, defaultTabletMode: boolean): GlideThunk {
    return (dispatch, getStore) => {
        const store = getStore();
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, store.apps);
        if (serializedApp === undefined) return;
        dispatch(updateSerializedApp(appID, { features: { ...getAppFeatures(serializedApp), defaultTabletMode } }));
    };
}

export function updateUseCustomSignInBackgroundImageInLoading(
    appID: string,
    useCustomSignInBackgroundImageInLoading: boolean
): GlideThunk {
    return (dispatch, getStore) => {
        const store = getStore();
        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, store.apps);
        if (serializedApp === undefined) return;
        dispatch(
            updateSerializedApp(appID, {
                features: {
                    ...getAppFeatures(serializedApp),
                    useCustomSignInBackgroundImageInLoading,
                },
            })
        );
    };
}

export function setReloadIssues(appID: string, reloadIssues: CompatibilityProblems | undefined): GlideThunk {
    return dispatch => {
        dispatch(updateApp(appID, { updates: { reloadIssues }, didModify: false }));
    };
}

function setAppIDs(userData: UserData, appIDs: string[]): GlideThunk {
    return dispatch => {
        const newUser = { ...userData, appIDs };
        dispatch(setUser(newUser));
    };
}

function getUserData(store: RootState): UserData | undefined {
    const { userData } = store;
    if (!isUserData(userData)) {
        logError("Not logged in - cannot add app");
        return undefined;
    }
    return userData;
}

export function addAppToOwner(appID: string, organizationID: string | undefined): GlideThunk {
    return (dispatch, getStore) => {
        if (organizationID === undefined) {
            const userData = getUserData(getStore());
            if (userData === undefined) return;

            const { appIDs } = userData;
            if (appIDs.indexOf(appID) >= 0) return;
            dispatch(setAppIDs(userData, appIDs.concat(appID)));
        } else {
            dispatch(addApp({ orgID: organizationID, appID }));
        }
    };
}

export function copySampleApp(
    sampleAppID: string,
    callback: (appID: string | undefined) => void,
    elideAccessChecks: boolean,
    targetOwnerID?: string
): GlideAsyncThunk {
    return (dispatch, getStore) =>
        withBusy(async (setNotBusy, setBusy) => {
            assert(isSample(sampleAppID));

            // eslint-disable-next-line prefer-const
            let { apps, userData } = getStore();

            if (!isUserData(userData)) {
                logError("Not logged in - cannot copy sample");
                callback(undefined);
                return;
            }

            while (!getStore().organizationsLoaded) {
                await sleep(1000);
            }

            let ownerID = isUserOrgID(targetOwnerID) ? userData.id : targetOwnerID;

            let { sourcePad, serializedApp } = getCurrentAppAndStateOrDefaults(sampleAppID, apps);
            if (serializedApp === undefined) {
                // Linters may warn you that this is a useless 'await'.
                // These linters are wrong. This await is necessary.
                await dispatch(loadAppFromFirebase(sampleAppID, false, false));
                apps = getStore().apps;
                userData = getStore().userData;
                const v = getCurrentAppAndStateOrDefaults(sampleAppID, apps);
                sourcePad = v.sourcePad;
                serializedApp = v.serializedApp;
            }
            if (serializedApp === undefined || sourcePad === undefined) return;
            assert(isUserData(userData));

            // Take note that the ordering here is critical: templates _are_ serialized apps
            // and if we didn't have them loaded before, we won't have them in the "templateStore".
            // But loading them puts them in the templateStore, so now we're clear to check
            // against it.
            const { templateStore } = getStore();

            const storeInfo = templateStore[sampleAppID];
            const noOrgsForUser = userData.orgUserIDs.length === 0;

            const templateIsFirstRun = isFirstRunBuilderTemplate(sampleAppID);
            const templateIsFirstRunAndNoOrgs = templateIsFirstRun && noOrgsForUser;

            // We have to elide access checks in the event of a race condition between
            // the template purchase window and our window. The race conditions are real:
            // our Firestore connection is shut off when we're not visible.
            if (storeInfo !== undefined && !elideAccessChecks && !templateIsFirstRunAndNoOrgs) {
                // Pop the purchase modal. The listener will update our license collection.
                // We're likely going to be called again by some responder if we complete
                // the purchase flow. That responder needs to set `elideAccessChecks = true`
                // to mitigate the race condition between our being called and the license
                // showing up in the user state.
                const { title, author, pricing } = storeInfo;

                if (pricing.value !== 0 || !noOrgsForUser) {
                    const { ownerID: modalOwnerID, nextAction } = await showBuyOrDownloadTemplateModal(sampleAppID, {
                        title,
                        author,
                        pricing,
                        iconImage: serializedApp.iconImage,
                        primaryAccentColor: serializedApp.theme.primaryAccentColor,
                    });

                    // This returns a boolean if we cannot yet copy the template, or a string identifying
                    // the target owner if we can copy the template.
                    if (nextAction !== "copy-template") {
                        // If the boolean value is true, we need to show Stripe Checkout for the purchase.
                        if (nextAction === "show-checkout") {
                            window.open(routes.purchaseTemplate(sampleAppID, modalOwnerID, uuid()), "_blank");
                        }
                        // This is also a fallthrough for "do nothing".
                        callback(undefined);
                        return;
                    }
                    ownerID = modalOwnerID;
                }
            }

            trackEvent("copy sample", { app_id: sampleAppID });

            const appFacilities = getAppFacilities();
            let appID: string | undefined;
            let message: string | undefined;

            let firstTry = true;
            for (;;) {
                try {
                    // We don't worry about fixing the app description for the new sheet ID here,
                    // the endpoint will do that for us.
                    const body: CreateAppFromTemplateBody = {
                        sourceAppID: sampleAppID,
                        appDescription: serializedApp,
                        ownerID,
                    };
                    const instantiationResponse = await appFacilities.callAuthCloudFunction(
                        "createAppFromTemplate",
                        body
                    );
                    if (isResponseOK(instantiationResponse)) {
                        appID = checkString((await instantiationResponse.json()).appID);
                    } else if (firstTry && instantiationResponse?.status === 403) {
                        setNotBusy();
                        const result = await firebaseAuth.ensureGoogleAccessTokenIsPresent();
                        setBusy();
                        if (!result.success) return;

                        firstTry = false;
                        continue;
                    } else {
                        message = await getResponseErrorMessage(instantiationResponse);
                    }
                } catch (e: unknown) {
                    logError(`Calling createAppFromTemplate: ${e}`);
                }
                break;
            }

            if (appID === undefined) {
                dontAwait(showErrorModal(`Could not copy template${message !== undefined ? `: ${message}` : ""}`));
                callback(undefined);
                return;
            }

            // Apologies for the awkwardness of this. The owner needs to be undefined
            // if it's not an org.
            dispatch(addAppToOwner(appID, ownerID === userData.id ? undefined : ownerID));
            callback(appID);
        });
}

export function unloadNonSampleApps(keepAppIDs: ReadonlyArray<string>): GlideThunk {
    return (dispatch, getStore) => {
        const { apps } = getStore();
        const appIDs = Object.keys(apps).filter(
            id => !isSample(id) && (keepAppIDs.indexOf(id) < 0 || apps[id] === "loading")
        );
        dispatch(unloadApps(appIDs));
    };
}

export function updateAppsWithUserFeatures(userFeatures: UserFeatures): GlideThunk {
    return (dispatch, getStore) => {
        const { apps } = getStore();
        for (const appID of Object.keys(apps)) {
            const app = getStore().apps[appID];
            if (app === undefined || app === "loading") continue;
            const { features } = app.serializedApp;
            if (features === undefined) continue;
            const newFeatures = updateDefined(features, userFeatures);
            if (deepEqual(features, newFeatures, { strict: true })) continue;

            dispatch(updateSerializedApp(appID, { features: newFeatures }));
        }
    };
}

export function upgradeAppToPlan(
    appID: string,
    plan: AppPlanCode,
    userCount: number | undefined,
    paymentRequestInfo: PaymentRequestInfo
): GlideAsyncThunk<boolean> {
    return async (dispatch, getStore) => {
        const { userData, organizations, apps } = getStore();

        if (!isUserData(userData)) return false;

        const owner = getAppOwner(appID, userData, organizations);

        if (owner === undefined) return false;

        const appAndState = getCurrentAppAndState(appID, apps);
        if (appAndState === undefined) return false;

        let serializedApp = appAndState.serializedApp;
        const auth = serializedApp.authentication;
        const targetFlags = getEminenceFlagsForAppPlanCode(owner, plan);
        const userProfileTableName = getUserProfileTableInfo(serializedApp)?.tableName;

        let mustChangeAuth = false;
        if (auth === undefined) {
            // No Auth, totally public
            mustChangeAuth = targetFlags.authenticationSupport.public !== true;
        } else {
            if (auth.kind === AppAuthenticationKind.EmailPin) {
                if (auth.source === AuthenticationEmailSource.OrgMembers) {
                    // Org Members Only
                    mustChangeAuth = targetFlags.authenticationSupport.organizationMembers !== true;
                } else if (auth.source === AuthenticationEmailSource.Table && auth.emailTableName === undefined) {
                    // Public with Email Auth
                    mustChangeAuth = targetFlags.authenticationSupport.publicWithEmail !== true;
                } else if (auth.emailTableName !== undefined) {
                    // Email Whitelist
                    mustChangeAuth = targetFlags.authenticationSupport.emailWhitelist !== true;
                } else {
                    // Just Me
                    mustChangeAuth = targetFlags.authenticationSupport.justMe !== true;
                }
            } else if (
                auth.kind === AppAuthenticationKind.Password ||
                (auth.kind === undefined && !isEmptyOrUndefined(auth.password))
            ) {
                // Password
                mustChangeAuth = targetFlags.authenticationSupport.password !== true;
            }
        }

        if (mustChangeAuth) {
            if (targetFlags.confirmChangeAuthOnUpgrade) {
                const doChange = await showAcceptCancelModal(
                    "Update authentication method?",
                    "The plan you have selected does not support your apps current authentication method. To continue, Glide must automatically adjust your authentication to one supported by the new plan.",
                    "Change",
                    "Cancel"
                );

                if (!doChange) {
                    return false;
                }
            }

            if (targetFlags.authenticationSupport.organizationMembers === true) {
                await dispatch(
                    setAppAuthentication(appID, {
                        ...serializedApp.authentication,
                        kind: AppAuthenticationKind.EmailPin,
                        source: AuthenticationEmailSource.OrgMembers,
                        emailTableName: undefined,
                    })
                );
            } else if (targetFlags.authenticationSupport.publicWithEmail === true) {
                await dispatch(
                    setAppAuthentication(appID, {
                        ...serializedApp.authentication,
                        kind: AppAuthenticationKind.EmailPin,
                        source:
                            userProfileTableName !== undefined
                                ? AuthenticationEmailSource.UserProfiles
                                : AuthenticationEmailSource.Table,
                        emailTableName: undefined,
                    })
                );
            } else if (targetFlags.authenticationSupport.justMe === true) {
                await dispatch(
                    setAppAuthentication(appID, {
                        ...serializedApp.authentication,
                        kind: AppAuthenticationKind.EmailPin,
                        source: AuthenticationEmailSource.JustMe,
                        emailTableName: undefined,
                    })
                );
            } else if (targetFlags.authenticationSupport.password === true) {
                await dispatch(
                    setAppAuthentication(appID, {
                        ...serializedApp.authentication,
                        kind: AppAuthenticationKind.Password,
                        password: makeRandomPassword(),
                    })
                );
            }

            // we just changed the serialized app so we have to reload it from the store or we will end up accidentally
            // nuking the change we just made further down the file
            const newStore = getStore();
            const newAppAndState = getCurrentAppAndState(appID, newStore.apps);
            if (newAppAndState === undefined) return false;
            serializedApp = newAppAndState.serializedApp;
        }

        let didUpgrade = false;
        let setAppPlanUnifiedResponse = await setAppPlanUnified(appID, plan, userCount, undefined);

        /**
         * This is most likely a legacy path, we might not even go through here any more.
         */
        if (setAppPlanUnifiedResponse.status === 402) {
            // Update the card
            const { setupIntentClientSecret } = setAppPlanUnifiedResponse;
            if (setupIntentClientSecret === undefined) {
                logError("The response should have a payment setup intent");
                return false;
            }

            const paymentMethodID = await showPaymentModal(setupIntentClientSecret, paymentRequestInfo);
            if (paymentMethodID === false) {
                logError("The response should have a payment setup intent");
                return false;
            }

            // Update the subscription
            setAppPlanUnifiedResponse = await setAppPlanUnified(appID, plan, userCount, paymentMethodID);
        }
        if (setAppPlanUnifiedResponse.ok) {
            const analyticsInfo = splitPlanCodeForAnalytics(plan);
            if (analyticsInfo !== undefined) {
                trackEvent("upgrade", {
                    app_id: appID,
                    ...analyticsInfo,
                    "private-users": analyticsInfo.privacy === "private" ? userCount : undefined,
                });
            }
            didUpgrade = true;

            // NOTE: Reload the app features to sync the serialized app with the new upgraded app feature set
            // FIXME: This is a potential race condition if the owner document (source of truth)
            // is not mutated before this is run. We could get a stale local state
            const appEnvironment = getAppEnvironmentForAppID(appID);
            if (appEnvironment !== undefined) {
                const { sourcePad, builderActions, publishState } = appAndState;
                dispatch(
                    updateAppDescription(
                        appEnvironment,
                        sourcePad,
                        {
                            ...serializedApp,
                            features: makeAppFeatures(
                                userData,
                                appID,
                                publishState !== "loading" && publishState.isPublished,
                                undefined,
                                newAppNonUserAppFeatures,
                                getAppKind(serializedApp)
                            ),
                        },
                        builderActions,
                        true
                    )
                );
            }
        }

        if (!didUpgrade) {
            trackEvent("upgrade failed", { app_id: appID });
        }

        return didUpgrade;
    };
}

export function downgradeApp(appID: string): GlideAsyncThunk {
    return async dispatch => {
        const { ok } = await setAppPlan(appID, "free");
        if (ok) {
            trackEvent("downgrade", { app_id: appID });
            dispatch(downgradeToFree(appID));
        }
        await showSuccessOrFailureModal(ok, "Your app is no longer Pro.", "Something went wrong.");
    };
}

export function navigateToDashboard(orgID: string = "0"): GlideThunk {
    return (_dispatch, _getStore, history) => {
        if (history !== undefined) {
            history.push(routes.org(orgID));
        }
    };
}

export function signOut(): GlideAsyncThunk {
    return async dispatch => {
        dispatch(unloadNonSampleApps([]));
        dispatch(unloadOrganizations());
        dispatch(setUser(null));
        await firebaseAuth.signOut();

        dispatch(navigateToDashboard());
    };
}
